import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useAutosave } from 'react-autosave';
import { useFormContext, useWatch } from 'react-hook-form';
import { unstable_usePrompt } from 'react-router-dom';

import { analytics } from 'analytics';
import { useGlobalModalsContext } from 'contexts/globalModalsContext';
import { useUpstreamFormContext } from 'contexts/upstreamFormContext';
import JSInputParser from 'diagram/jsInputParser';
import { Sankey } from 'generated-api';

import { useAppEnvironmentContext } from '../contexts/appEnvironmentContext';
import { useSankeyFormContext } from '../contexts/sankeyFormContext';
import { useSankeyRenderContext } from '../contexts/sankeyRenderContext';
import { getChartFromSankeyFormValues } from './chart';
import {
  convertToMaticInput,
  emptySankey,
  sankeyFormFlowsArrayToMap,
  sankeyFormFlowsMapToArray,
  sankeyFormNodesArrayToMap,
  sankeyFormNodesMapToArray,
} from './sankey';

export const useCurrentFormSankeyId = (): number | undefined => {
  const { form } = useSankeyFormContext();
  return form?.getValues('id');
};

export const useSankeySyncer = ({
  callback,
  debounceMs,
  isEnabled = true,
}: {
  callback: () => void;
  debounceMs: number;
  isEnabled?: boolean;
}) => {
  const { form } = useSankeyFormContext();

  // We encode all the form values into a single string to make sure they are deep compared for changes.
  const sankeyEncodedData = JSON.stringify(useWatch(form) ?? {});

  // NOTE: Normally we would use the formState.isDirty directly to check if the form
  // has been modified, however this would require us to reset the form state after
  // each successful save which might mess up the form values user has been modifying
  // while the save was in progress. Instead, we want to ensure one-direction data save flow
  // UI -> backend for simplicity but that means isDirty will sometimes be giving false
  // negatives after the first save. To resolve this, we say the form is always modified
  // after the first save and instead rely on sankeyEncodedData value to decide whether
  // to actually trigger the callback or not.
  const isModified = form.formState.isDirty || form.formState.isSubmitted;

  useAutosave({
    data: sankeyEncodedData,
    interval: debounceMs,
    onSave: () => {
      // Trigger callback only if syncing is enabled and the form has been modified.
      if (isEnabled && isModified) {
        callback();
      }
    },
  });
};

const beforeUnloadMessage =
  'Are you sure you want to leave before saving your work? If you leave before saving, your changes will be lost.';

// Borrowed from https://stackoverflow.com/a/7317311 (we have form autosave so can use the most naive solution).
const beforeUnloadPrompt = (e: BeforeUnloadEvent) => {
  (e || window.event).returnValue = beforeUnloadMessage; // Gecko + IE
  return beforeUnloadMessage; // Gecko + Webkit, Safari, Chrome etc.
};

export const useConditionalLeavePagePrompt = () => {
  const { isAuthOrConversionModalOpen } = useGlobalModalsContext();
  const { appEnvironment } = useAppEnvironmentContext();
  const { hasModified } = useSankeyRenderContext();
  const { canUpdate } = useSankeyFormContext();

  // Prompt the user only if they've made changes that won't be saved (this currently only happens
  // when users are anonymous). Also, show this only in the web app context since Office add-in blocks
  // window.confirm which is necessary for this to work. Don't show the prompt if in conversion modal.
  // See https://github.com/sankeyart/sankeyart/issues/1710.
  const shouldPrompt =
    hasModified &&
    !canUpdate &&
    !isAuthOrConversionModalOpen &&
    appEnvironment === 'web';

  // Warn the user if they try to leave the page without saving to make sure they don't lose their work
  // See https://stackoverflow.com/questions/7317273/warn-user-before-leaving-web-page-with-unsaved-changes
  useEffect(() => {
    if (shouldPrompt) {
      window.removeEventListener('beforeunload', beforeUnloadPrompt);
      window.addEventListener('beforeunload', beforeUnloadPrompt);
    }
  }, [shouldPrompt]);
  // And use the same warning when navigating away internally to a different React Router path.
  unstable_usePrompt({
    when: shouldPrompt,
    message: beforeUnloadMessage,
  });
};

export const removeLeavePagePrompt = () => {
  window.removeEventListener('beforeunload', beforeUnloadPrompt);
};

/*
 * Returns the SVG representation of the Sankey diagram without the font <defs> tag.
 */
const getBareSVGRepresentation = () => {
  const representation = document.getElementById('sankey_svg')?.outerHTML ?? '';
  return representation.replace(/<defs>[\s\S]*?<\/defs>/g, '');
};

/*
 * Converts Sankey form data into the format expected by save / create Sankey API.
 */
export const formToSankey = (form: SankeyForm): WithExplicitFields<Sankey> => ({
  /* Copied values */
  canvas_height: form.getValues('canvas_height'),
  canvas_size: form.getValues('canvas_size'),
  canvas_width: form.getValues('canvas_width'),
  chart_type_list_position: form.getValues('chart_type_list_position'),
  chart_type_name: form.getValues('chart_type_name'),
  created_at: form.getValues('created_at'),
  created_with: form.getValues('created_with'),
  company_name: form.getValues('company_name'),
  comparison_line_prefix: form.getValues('comparison_line_prefix'),
  comparison_line_suffix: form.getValues('comparison_line_suffix'),
  comparison_line_value_type: form.getValues('comparison_line_value_type'),
  custom_title: form.getValues('custom_title'),
  data_origin: form.getValues('data_origin'),
  data_source_notes: form.getValues('data_source_notes'),
  download_image_format: form.getValues('download_image_format'),
  download_scale_png: form.getValues('download_scale_png'),
  fiscal_year: form.getValues('fiscal_year'),
  font_family: form.getValues('font_family'),
  font_size: form.getValues('font_size'),
  id: form.getValues('id'),
  is_example: form.getValues('is_example'),
  is_listed_chart_type_in_footer: form.getValues(
    'is_listed_chart_type_in_footer'
  ),
  is_main_example: form.getValues('is_main_example'),
  is_official: form.getValues('is_official'),
  is_template: form.getValues('is_template'),
  label_pos: form.getValues('label_pos'),
  node_height: form.getValues('node_height'),
  node_spacing: form.getValues('node_spacing'),
  node_width: form.getValues('node_width'),
  nr_of_decimals: form.getValues('nr_of_decimals'),
  owner_id: form.getValues('owner_id'),
  points: form.getValues('points'),
  public_permission: form.getValues('public_permission'),
  remembered_node_moves: form.getValues('remembered_node_moves'),
  reporting_period: form.getValues('reporting_period'),
  second_label_line_value_type: form.getValues('second_label_line_value_type'),
  show_canvas_resolution: form.getValues('show_canvas_resolution'),
  show_comparison_line: form.getValues('show_comparison_line'),
  show_data_editor: form.getValues('show_data_editor'),
  show_imbalances: form.getValues('show_imbalances'),
  show_labels: form.getValues('show_labels'),
  show_title: form.getValues('show_title'),
  show_watermark: form.getValues('show_watermark'),
  statement_type: form.getValues('statement_type'),
  team_permission: form.getValues('team_permission'),
  title_font_color: form.getValues('title_font_color'),
  title_font_size: form.getValues('title_font_size'),
  unit_prefix: form.getValues('unit_prefix'),
  unit_suffix: form.getValues('unit_suffix'),
  use_custom_title: form.getValues('use_custom_title'),
  /* Overrides */
  js_input: convertToMaticInput(
    sankeyFormFlowsMapToArray(form.getValues('flows')),
    sankeyFormNodesMapToArray(form.getValues('nodes'))
  ),
  chart: getChartFromSankeyFormValues(form.getValues()),
  // Pass the SVG representation to the backend without the <defs> tag
  svg_representation: getBareSVGRepresentation(),
});

/*
 * Converts Sankey model to the form data.
 */
export const sankeyToForm = (sankey: Sankey): SankeyFormValues => {
  const parsed = new JSInputParser(sankey.js_input ?? '').parse(
    (sourceLine) => {
      analytics.reportError('Diagram parsing error', { sourceLine }, false);
    }
  );
  const formValues = {
    ...sankey,
    nodes: sankeyFormNodesArrayToMap(parsed.nodes),
    flows: sankeyFormFlowsArrayToMap(
      parsed.flows.map((flow) => ({
        ...flow,
        source: flow.source.name,
        target: flow.target.name,
      }))
    ),
    // Use fallback values for custom canvas width and height to make sure we always have valid sizes defined
    canvas_width: sankey.canvas_width || 960,
    canvas_height: sankey.canvas_height || 600,
  };
  // Return form values with populated chart JSON field
  return { ...formValues, chart: getChartFromSankeyFormValues(formValues) };
};

export const emptySankeyFormValues = sankeyToForm(emptySankey);
export const emptySankeyFormState = JSON.stringify(emptySankeyFormValues);

/**
 * Hook that propagates changes from a field in a draft form to the corresponding field
 * in the upstream form. This is useful for nested forms where changes need to be synced
 * back to parent autosave-tied form after the draft form is made sure to be valid.
 *
 * @param field - Path to the form field that should be propagated to the upstream form
 * @param delayMs - Delay in milliseconds before propagating the value (for debouncing)
 */
export const useUpstreamFormPropagation = (
  field: SankeyFormPath,
  delayMs: number
) => {
  const draftForm = useFormContext<SankeyFormValues>();
  const { upstreamForm } = useUpstreamFormContext();
  const enabled = !!upstreamForm;

  // Debounced function to propagate the value from the
  // draft form to the upstream form if the field is valid.
  const debouncedPropagateToOriginalForm = useMemo(
    () =>
      debounce(() => {
        draftForm.trigger(field).then((isValid) => {
          if (isValid) {
            upstreamForm?.setValue(field, draftForm.getValues(field), {
              shouldDirty: true,
            });
          }
        });
      }, delayMs),
    [field, delayMs]
  );

  useEffect(() => {
    // Propagate values from the draft form to the upstream form if it exists
    if (enabled) {
      draftForm.register(field, {
        onChange: () => debouncedPropagateToOriginalForm(),
      });
    }
    // Cleanup function to unregister the fields and cancel the debounced
    // function when the component unmounts or the field changes.
    return () => {
      if (enabled) {
        draftForm.unregister(field);
      }
      debouncedPropagateToOriginalForm.cancel();
    };
  }, [field, enabled, debouncedPropagateToOriginalForm]);
};
